/**
 * SpriteRenderer.ts — Image-based sprite rendering on a canvas overlay.
 *
 * Loads PNG sprite images and draws them on a canvas positioned over the
 * xterm terminal. Sprites are pre-loaded at boot and drawn on demand at
 * terminal row/col positions, scaled to fit within panel regions.
 *
 * Background removal: Since Nova Canvas doesn't generate transparent PNGs,
 * sprites are drawn to an offscreen canvas first where the background color
 * is stripped to transparent, then composited onto the visible overlay.
 *
 * Resize persistence: The renderer tracks the last drawInPanel() call and
 * automatically redraws on container resize so the sprite stays visible.
 *
 * Adapted from RotaryKnights for Gallowcrest — sprite URLs registered dynamically
 * since sprites are generated separately.
 */

import type { Terminal } from './Terminal';

/** Map of sprite name → image URL (populated by imports or registerSprite) */
const SPRITE_URLS: Record<string, string> = {};

/** State needed to redraw the current sprite on resize */
interface DrawState {
    spriteName: string;
    startRow: number;
    leftCol: number;
    panelWidth: number;
    maxHeightRows?: number;
}

export class SpriteRenderer {
    private terminal: Terminal;
    private canvas: HTMLCanvasElement | null = null;
    private ctx: CanvasRenderingContext2D | null = null;
    private container: HTMLElement | null = null;
    private images: Map<string, HTMLImageElement> = new Map();
    private cleanImages: Map<string, HTMLCanvasElement> = new Map();
    private loadPromises: Map<string, Promise<HTMLImageElement>> = new Map();
    private currentDraw: DrawState | null = null;
    private resizeTimer: ReturnType<typeof setTimeout> | null = null;

    constructor(terminal: Terminal) {
        this.terminal = terminal;
    }

    /** Register a sprite name → URL mapping for later loading. */
    static registerSprite(name: string, url: string): void {
        SPRITE_URLS[name] = url;
    }

    /** Register multiple sprites at once. */
    static registerSprites(sprites: Record<string, string>): void {
        for (const [name, url] of Object.entries(sprites)) {
            SPRITE_URLS[name] = url;
        }
    }

    /** Attach to the terminal container and preload all registered sprites */
    attach(container: HTMLElement): void {
        this.container = container;
        this.canvas = document.createElement('canvas');
        this.canvas.style.cssText = [
            'position: absolute',
            'top: 0',
            'left: 0',
            'width: 100%',
            'height: 100%',
            'pointer-events: none',
            'z-index: 10', // Ensure above terminal
            'display: none',
        ].join(';');
        this.ctx = this.canvas.getContext('2d')!;
        container.appendChild(this.canvas);

        const ro = new ResizeObserver(() => this.onResize());
        ro.observe(container);

        // Preload all registered sprites
        for (const [name, url] of Object.entries(SPRITE_URLS)) {
            this.preload(name, url);
        }
    }

    private onResize(): void {
        if (this.resizeTimer) clearTimeout(this.resizeTimer);
        this.resizeTimer = setTimeout(() => {
            this.syncSize();
            if (this.currentDraw) {
                this.redrawCurrent();
            }
        }, 50);
    }

    private syncSize(): void {
        if (!this.canvas || !this.container) return;
        const rect = this.container.getBoundingClientRect();
        const dpr = window.devicePixelRatio || 1;
        this.canvas.width = Math.round(rect.width * dpr);
        this.canvas.height = Math.round(rect.height * dpr);
    }

    private preload(name: string, url: string): Promise<HTMLImageElement> {
        if (this.loadPromises.has(name)) return this.loadPromises.get(name)!;
        const p = new Promise<HTMLImageElement>((resolve, reject) => {
            const img = new Image();
            img.onload = () => {
                this.images.set(name, img);
                this.buildCleanImage(name, img);
                resolve(img);
            };
            img.onerror = () => {
                reject(new Error(`Failed to load sprite "${name}" from ${url}`));
            };
            img.src = url;
        });
        this.loadPromises.set(name, p);
        return p;
    }

    /**
     * Build a background-removed version of the sprite.
     */
    private buildCleanImage(name: string, img: HTMLImageElement): void {
        const offscreen = document.createElement('canvas');
        offscreen.width = img.width;
        offscreen.height = img.height;
        const octx = offscreen.getContext('2d')!;
        octx.drawImage(img, 0, 0);

        const data = octx.getImageData(0, 0, img.width, img.height);
        const px = data.data;

        const corners = [
            0,
            (img.width - 1) * 4,
            (img.height - 1) * img.width * 4,
            ((img.height - 1) * img.width + img.width - 1) * 4,
        ];

        let bgR = 0, bgG = 0, bgB = 0, count = 0;
        for (const i of corners) {
            if (px[i + 3] > 0) {
                bgR += px[i];
                bgG += px[i + 1];
                bgB += px[i + 2];
                count++;
            }
        }
        if (count === 0) {
            this.cleanImages.set(name, offscreen);
            return;
        }
        bgR = Math.round(bgR / count);
        bgG = Math.round(bgG / count);
        bgB = Math.round(bgB / count);

        const tolerance = 35;

        for (let i = 0; i < px.length; i += 4) {
            const dr = Math.abs(px[i] - bgR);
            const dg = Math.abs(px[i + 1] - bgG);
            const db = Math.abs(px[i + 2] - bgB);
            if (dr <= tolerance && dg <= tolerance && db <= tolerance) {
                px[i + 3] = 0;
            }
        }

        octx.putImageData(data, 0, 0);
        this.cleanImages.set(name, offscreen);
    }

    // ── Cell geometry ──────────────────────────────────────────

    private getCellSize(): { cellW: number; cellH: number } {
        const raw = this.terminal.getRawTerminal() as any;
        const dims = raw._core?._renderService?.dimensions;
        if (dims?.css?.cell) {
            return { cellW: dims.css.cell.width, cellH: dims.css.cell.height };
        }
        const { cols, rows } = this.terminal.getSize();
        const rect = this.container?.getBoundingClientRect();
        if (rect && cols > 0 && rows > 0) {
            return { cellW: rect.width / cols, cellH: rect.height / rows };
        }
        const fontSize = this.terminal.getFontSize();
        return { cellW: fontSize * 0.6, cellH: fontSize * 1.2 };
    }

    private cellToPixel(row: number, col: number): { x: number; y: number } {
        const { cellW, cellH } = this.getCellSize();
        return { x: (col - 1) * cellW, y: (row - 1) * cellH };
    }

    // ── Public API ─────────────────────────────────────────────

    /**
     * Draw a sprite centered in a panel region.
     * Returns the number of terminal rows the sprite occupies.
     */
    async drawInPanel(
        spriteName: string,
        startRow: number,
        leftCol: number,
        panelWidth: number,
        maxHeightRows?: number,
    ): Promise<number> {
        const url = SPRITE_URLS[spriteName];
        if (!url) {
            console.warn(`[SpriteRenderer] No URL for sprite: ${spriteName}`);
            return 0;
        }
        const img = this.images.get(spriteName) ?? await this.preload(spriteName, url);
        if (!img || !this.canvas || !this.ctx) {
            console.error(`[SpriteRenderer] Failed to load sprite: ${spriteName}`);
            return 0;
        }

        console.log(`[SpriteRenderer] Rendering ${spriteName} at row ${startRow}, maxHeightRows ${maxHeightRows ?? 'auto'}`);
        this.currentDraw = { spriteName, startRow, leftCol, panelWidth, maxHeightRows };
        return this.renderSprite(spriteName, startRow, leftCol, panelWidth, maxHeightRows);
    }

    private renderSprite(
        spriteName: string,
        startRow: number,
        leftCol: number,
        panelWidth: number,
        maxHeightRows?: number,
    ): number {
        const source: CanvasImageSource | undefined =
            this.cleanImages.get(spriteName) ?? this.images.get(spriteName);
        if (!source || !this.canvas || !this.ctx) return 0;

        this.canvas.style.display = 'block';
        this.syncSize();

        const { cellW, cellH } = this.getCellSize();
        const dpr = window.devicePixelRatio || 1;
        const ctx = this.ctx;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

        ctx.clearRect(0, 0, this.canvas.width / dpr, this.canvas.height / dpr);

        const availW = panelWidth * cellW;
        const terminalH = this.container?.getBoundingClientRect().height ?? availW;
        const maxH = maxHeightRows ? maxHeightRows * cellH : Math.min(availW * 0.5, terminalH * 0.4);

        const srcW = (source as HTMLCanvasElement).width ?? (source as HTMLImageElement).naturalWidth;
        const srcH = (source as HTMLCanvasElement).height ?? (source as HTMLImageElement).naturalHeight;
        const imgAspect = srcW / srcH;

        let drawW = Math.min(availW * 0.7, availW);
        let drawH = drawW / imgAspect;

        if (drawH > maxH) {
            drawH = maxH;
            drawW = drawH * imgAspect;
        }

        if (drawW > availW) {
            drawW = availW;
            drawH = drawW / imgAspect;
        }

        const panelOrigin = this.cellToPixel(startRow, leftCol);
        const offsetX = (availW - drawW) / 2;

        const clipX = (leftCol - 1) * cellW;
        const clipY = (startRow - 1) * cellH;
        const clipW = panelWidth * cellW;
        const clipH = maxHeightRows
            ? maxHeightRows * cellH
            : (this.canvas.height / dpr) - clipY;

        ctx.save();
        ctx.beginPath();
        ctx.rect(clipX, clipY, clipW, clipH);
        ctx.clip();

        ctx.imageSmoothingEnabled = false;
        ctx.drawImage(source, panelOrigin.x + offsetX, panelOrigin.y, drawW, drawH);

        ctx.restore();

        return Math.ceil(drawH / cellH);
    }

    private redrawCurrent(): void {
        if (!this.currentDraw) return;
        const { spriteName, startRow, leftCol, panelWidth, maxHeightRows } = this.currentDraw;
        this.renderSprite(spriteName, startRow, leftCol, panelWidth, maxHeightRows);
    }

    /** Clear the canvas */
    clear(): void {
        if (!this.canvas || !this.ctx) return;
        this.ctx.setTransform(1, 0, 0, 1, 0, 0);
        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
        this.currentDraw = null;
    }

    /** Hide the canvas overlay */
    hide(): void {
        this.clear();
        if (this.canvas) this.canvas.style.display = 'none';
    }

    /** Check if a sprite exists */
    hasSprite(name: string): boolean {
        return name in SPRITE_URLS;
    }

    /** Estimated terminal rows for a sprite in a given panel width */
    estimateRows(spriteName: string, panelWidth: number): number {
        const img = this.images.get(spriteName);
        if (!img) return 8;
        const { cellW, cellH } = this.getCellSize();
        const drawW = panelWidth * cellW * 0.7;
        let drawH = drawW / (img.width / img.height);
        const terminalH = this.container?.getBoundingClientRect().height ?? drawW;
        const maxH = Math.min(drawW * 0.5 / 0.7, terminalH * 0.4);
        if (drawH > maxH) drawH = maxH;
        return Math.ceil(drawH / cellH);
    }
}
